/**
 * Workload-Steckbrief API client (NC-1279).
 *
 * Mirrors the trainingFilesService shape: thin wrappers that unwrap the
 * { success, data } envelope so components never see it.
 */

import apiClient from './api';
import {
  ApiResponse,
  ProfileAutoFacts,
  ProfileGapResult,
  WorkloadConclusion,
  WorkloadConclusionInput,
  WorkloadProfile,
  WorkloadProfileInput,
  WorkloadRetrospectives,
} from './types';

export interface WorkloadProfileResult {
  workloadId: string;
  profile: WorkloadProfile | null;
  /**
   * NC-1280: both null while no profile exists — there is nothing to measure
   * against. Optional so a pre-NC-1280 response still parses.
   */
  facts?: ProfileAutoFacts | null;
  gaps?: ProfileGapResult | null;
  /**
   * NC-1281: both retrospective stages in one payload — the server reads the
   * partition once and splits by `kind`. Optional so a pre-NC-1281 response
   * still parses.
   */
  retrospectives?: WorkloadRetrospectives | null;
}

/** A blank draft that already satisfies the server schema. */
export function emptyProfileInput(): WorkloadProfileInput {
  return {
    purpose: { goal: '', audience: '', audienceScope: 'intern', alternative: '' },
    ownership: {
      contact: { name: '', role: '', email: '' },
      deputy: null,
      department: '',
      costCenterCode: '',
    },
    usage: {
      frequency: 'einmalig',
      participantsTypical: 0,
      participantsMax: 0,
      durationDays: { min: 0, max: 0 },
      runtimeProfile: 'office-hours',
    },
    requirements: {
      licensing: '', hasInputData: false,
      outputTarget: 'none', language: 'de-DE', notes: '',
    },
    acceptance: { criterion: '' },
    openItems: [],
  };
}

export const workloadProfileApi = {
  async get(workloadId: string): Promise<WorkloadProfileResult> {
    const res = await apiClient.get<ApiResponse<WorkloadProfileResult>>(
      `/workloads/${encodeURIComponent(workloadId)}/profile`
    );
    if (!res.data.success || !res.data.data) {
      throw new Error(res.data.error || 'Steckbrief konnte nicht geladen werden');
    }
    return res.data.data;
  },

  async save(workloadId: string, input: WorkloadProfileInput): Promise<WorkloadProfile> {
    const res = await apiClient.put<ApiResponse<WorkloadProfile>>(
      `/workloads/${encodeURIComponent(workloadId)}/profile`,
      input
    );
    if (!res.data.success || !res.data.data) {
      throw new Error(res.data.error || 'Steckbrief konnte nicht gespeichert werden');
    }
    return res.data.data;
  },

  /**
   * NC-1281 S4: file a workload conclusion. Append-only by design — the server
   * mints a fresh `concl-<uuid>` on every call, so two conclusions by the same
   * author are two documents. There is deliberately no update and no delete:
   * divergent judgements are shown side by side, not resolved.
   *
   * operator+; 400 on an empty statement. `basedOnRunIds` may be empty — "never
   * used, need gone" is a legitimate conclusion.
   */
  async postConclusion(
    workloadId: string,
    input: WorkloadConclusionInput,
  ): Promise<WorkloadConclusion> {
    const res = await apiClient.post<ApiResponse<WorkloadConclusion>>(
      `/workloads/${encodeURIComponent(workloadId)}/conclusion`,
      input,
    );
    if (!res.data.success || !res.data.data) {
      throw new Error(res.data.error || 'Fazit konnte nicht gespeichert werden');
    }
    return res.data.data;
  },
};
